import { useEffect, useState } from "react";
import api from "../lib/api";
import { Trophy, Crown } from "lucide-react";
import { Link } from "react-router-dom";

function formatDollar(n) {
  return "$" + Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
}

export default function RollOfHonor() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/leaderboard", { params: { limit: 10 } })
      .then((r) => setItems(r.data))
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="relative border-t border-bdu-gold/10 py-28" data-testid="roll-of-honor-section">
      <div className="max-w-7xl mx-auto px-6 sm:px-10">
        <div className="flex items-end justify-between flex-wrap gap-6 mb-14">
          <div>
            <div className="label-eyebrow flex items-center gap-2">
              <Trophy size={14} /> Roll of Honor
            </div>
            <h2 className="mt-4 font-serif text-4xl sm:text-5xl text-bdu-cream tracking-tight">
              The founders of the fund.
            </h2>
          </div>
          <p className="max-w-md text-bdu-cream/60 text-sm">
            Members who opted to have their name recorded publicly. Every dollar builds our future.
            Add yours — <Link to="/pledge" className="text-bdu-gold hover:underline" data-testid="roll-honor-cta">pledge here</Link>.
          </p>
        </div>

        {loading ? (
          <div className="text-bdu-cream/50 py-16 text-center">Loading...</div>
        ) : items.length === 0 ? (
          <div className="bdu-card text-center py-14" data-testid="roll-of-honor-empty">
            <Crown size={32} strokeWidth={1.25} className="text-bdu-gold mx-auto" />
            <div className="mt-6 font-serif text-2xl text-bdu-cream">Be the first name in history.</div>
            <p className="mt-3 text-bdu-cream/60 max-w-md mx-auto">
              No public pledges yet. Opt in when you record your pledge to appear here.
            </p>
            <Link to="/pledge" className="btn-gold mt-8 inline-flex" data-testid="roll-honor-empty-cta">
              Make Your Pledge
            </Link>
          </div>
        ) : (
          <div className="border border-bdu-gold/10">
            <ol data-testid="roll-of-honor-list">
              {items.map((entry, i) => (
                <li
                  key={entry.rank}
                  className={`grid grid-cols-[auto_1fr_auto] items-center gap-6 px-6 sm:px-8 py-6 ${
                    i !== items.length - 1 ? "border-b border-bdu-gold/10" : ""
                  } ${entry.rank === 1 ? "bg-bdu-gold/[0.03]" : ""}`}
                  data-testid={`roll-honor-row-${entry.rank}`}
                >
                  <div className={`font-serif text-4xl leading-none tabular-nums ${
                    entry.rank === 1 ? "text-bdu-gold" : entry.rank <= 3 ? "text-bdu-cream" : "text-bdu-cream/40"
                  }`}>
                    {String(entry.rank).padStart(2, "0")}
                  </div>
                  <div>
                    <div className="flex items-center gap-3 flex-wrap">
                      <span className="font-serif text-xl sm:text-2xl text-bdu-cream">{entry.user_name}</span>
                      {entry.rank === 1 && <Crown size={16} className="text-bdu-gold" />}
                      {entry.is_member && (
                        <span
                          className="inline-flex items-center gap-1 border border-bdu-gold/50 bg-bdu-gold/10 px-2 py-0.5 text-[9px] uppercase tracking-[0.2em] text-bdu-gold"
                          data-testid={`founding-member-badge-${entry.rank}`}
                          title="Founding Member — paid $300 membership"
                        >
                          <Crown size={9} /> Founding Member
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-bdu-cream/40 tracking-widest uppercase">
                      {entry.pledge_count} pledge{entry.pledge_count === 1 ? "" : "s"}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-serif text-2xl sm:text-3xl text-bdu-gold tabular-nums">
                      {formatDollar(entry.total)}
                    </div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        )}
      </div>
    </section>
  );
}
